import { MediaStreamTransmissionStartedMessage, ModelMediaStreamEndedMessage, ModelMediaStreamStartedMessage } from "../../../messages/media";
import { peerService } from "./PeerService";
import { busService } from "../../bus";
import { PeerAddedMessage, PeerRemovedMessage } from "../../../messages/session/PeerMessages";
import { MulticastDelegate } from "flavr-common";
import { camera } from "../../rendering/camera";
export var StreamType;
(function (StreamType) {
    StreamType[StreamType["microphone"] = 0] = "microphone";
    StreamType[StreamType["camera"] = 1] = "camera";
    StreamType[StreamType["screen"] = 2] = "screen";
    StreamType[StreamType["player3dView"] = 3] = "player3dView";
})(StreamType || (StreamType = {}));
export class RemoteStreamingService {
    constructor() {
        this.bus = busService.connection("RemoteStreamingService");
        this.handleMediaStreamTransmissionStartedMessage = (msg, meta) => {
            if (!meta.clientId) {
                console.error("[multiplayer] CameraStreamStartedMessage without clientId");
                return;
            }
            const streamType = this.streamTypeFromString(msg.streamType);
            if (streamType === undefined) {
                console.error("[multiplayer] Invalid stream type", msg.streamType);
                return;
            }
            const pending = this.pendingStreams.find((pending) => pending.streamId === msg.streamId);
            if (pending && pending.stream) {
                // We already have a pending stream for this client, so complete it and add it to remoteStreams
                if (streamType === StreamType.screen || streamType === StreamType.player3dView) {
                    if (!msg.surfaceUid) {
                        console.error("[multiplayer] Screen stream without surfaceUid");
                        throw new Error("Screen stream without surfaceUid");
                    }
                    this.addNewRemoteStream({
                        clientId: meta.clientId,
                        stream: pending.stream,
                        type: streamType,
                        surfaceUid: msg.surfaceUid
                    });
                }
                else {
                    this.addNewRemoteStream({
                        clientId: meta.clientId,
                        stream: pending.stream,
                        type: streamType
                    });
                }
                this.pendingStreams.splice(this.pendingStreams.indexOf(pending), 1);
            }
            else if (!pending) {
                // We don't have a pending stream for this client yet, so add it to pendingStreams
                this.pendingStreams.push({
                    streamId: msg.streamId,
                    stream: undefined,
                    streamType: streamType,
                    surfaceUid: msg.surfaceUid
                });
                this.removePendingStreamIfTimedOut(msg.streamId);
            }
            else {
                console.error("[multiplayer] Invalid pending stream state");
            }
        };
        this.onUpdateDelegate = new MulticastDelegate();
        this.remoteStreams = [];
        this.globallyMuted = false;
        this.numberOfTriesToInitialize = 0;
        this.pendingStreams = [];
    }
    init() {
        this.numberOfTriesToInitialize = 0;
        // PeerService / PeerManager might not be initialized yet, so we do the initialization in a sub-function that can be repeated until things are available
        const doInit = () => {
            if (peerService && peerService.peerMgr) {
                this.bus.subscribeByType(PeerAddedMessage, (msg) => {
                    var _a;
                    const peer = (_a = peerService.peerMgr) === null || _a === void 0 ? void 0 : _a.getPeer(msg.clientId);
                    if (peer) {
                        for (const stream of peer.mediaConnections.mediaStreams) {
                            this.handleNewStreamForPeer(stream, msg.clientId);
                        }
                        peer.mediaConnections.onMediaStreamTrackAddedDelegate.add((stream, _track) => {
                            this.handleNewStreamForPeer(stream, msg.clientId);
                        });
                        peer.mediaConnections.onMediaStreamTrackRemovedDelegate.add((stream, _track) => {
                            this.handleMediaStreamEndedMessage(stream);
                        });
                    }
                    else {
                        console.error("[multiplayer] PeerAddedMessage without peer");
                    }
                });
                this.bus.subscribeByType(MediaStreamTransmissionStartedMessage, this.handleMediaStreamTransmissionStartedMessage);
                // Remove streams when a peer is removed
                this.bus.subscribeByType(PeerRemovedMessage, (msg) => {
                    const streams = this.remoteStreams.filter((stream) => stream.clientId === msg.clientId);
                    streams.forEach((remoteStream) => {
                        remoteStream.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        this.remoteStreams.splice(this.remoteStreams.indexOf(remoteStream), 1);
                        if (remoteStream.type === StreamType.screen) {
                            // In case of screen streams, we need to inform scripts about the end of the stream
                            const message = new ModelMediaStreamEndedMessage(remoteStream.surfaceUid);
                            this.bus.send(message);
                        }
                    });
                    this.onUpdateDelegate.invoke();
                });
            }
            else {
                this.numberOfTriesToInitialize++;
                if (this.numberOfTriesToInitialize > 15) {
                    console.warn("[multiplayer] Couldn't initialize service after several attempts, trying again");
                    this.numberOfTriesToInitialize = 0;
                }
                setTimeout(doInit, 100);
            }
        };
        doInit();
    }
    getRemoteStreamsByClientId(clientId) {
        return this.remoteStreams.filter((stream) => stream.clientId === clientId);
    }
    getMicrophoneStreams() {
        return this.remoteStreams.filter((stream) => stream.type === StreamType.microphone);
    }
    getCameraStreams() {
        return this.remoteStreams.filter((stream) => stream.type === StreamType.camera);
    }
    getScreenStreams() {
        return this.remoteStreams.filter((stream) => stream.type === StreamType.screen);
    }
    getPlayer3dViewStreams() {
        return this.remoteStreams.filter((stream) => stream.type === StreamType.player3dView);
    }
    getGloballyMuted() {
        return this.globallyMuted;
    }
    isClientMuted(clientId) {
        if (this.globallyMuted) {
            return true;
        }
        return this.getMicrophoneStreams()
            .filter((stream) => stream.clientId === clientId)
            .some((stream) => stream.forcedMute === true);
    }
    setGloballyMuted(muted) {
        this.globallyMuted = muted;
        // Update the master volume of the audio listener to force mute all incoming audio from local sources as well
        camera.audioListener.setMasterVolume(muted ? 0 : 1);
        this.onUpdateDelegate.invoke();
    }
    setClientMuted(clientId, muted) {
        const streams = this.getRemoteStreamsByClientId(clientId);
        streams.forEach((remoteStream) => {
            if (remoteStream.type !== StreamType.microphone) {
                return;
            }
            remoteStream.forcedMute = muted;
        });
        this.onUpdateDelegate.invoke();
    }
    removePendingStreamIfTimedOut(streamId) {
        // In case we never get the other half of the stream information, remove the pending stream after 5 seconds
        setTimeout(() => {
            const pending = this.pendingStreams.find((pending) => pending.streamId === streamId);
            if (pending) {
                this.pendingStreams.splice(this.pendingStreams.indexOf(pending), 1);
            }
        }, 30000);
    }
    // NOTE: This won't work for streams with multiple tracks (But that's currently not relevant, as we don't use the MediaStreams that way)
    handleNewStreamForPeer(stream, clientId) {
        // Remove the stream from remoteStreams when the track is removed
        var _a;
        stream.onremovetrack = () => {
            const remoteStream = this.remoteStreams.find((remoteStream) => remoteStream.stream.id === stream.id);
            if (remoteStream) {
                this.remoteStreams.splice(this.remoteStreams.indexOf(remoteStream), 1);
                this.onUpdateDelegate.invoke();
            }
        };
        const pending = this.pendingStreams.find((pending) => pending.streamId === stream.id);
        if (pending && pending.stream === undefined) {
            // We already have a pending stream for this client, so complete it and add it to remoteStreams
            if (pending.streamType === StreamType.screen || pending.streamType === StreamType.player3dView) {
                this.addNewRemoteStream({
                    clientId: clientId,
                    stream: stream,
                    type: pending.streamType,
                    surfaceUid: (_a = pending.surfaceUid) !== null && _a !== void 0 ? _a : 0
                });
            }
            else {
                this.addNewRemoteStream({
                    clientId: clientId,
                    stream: stream,
                    type: pending.streamType
                });
            }
            this.pendingStreams.splice(this.pendingStreams.indexOf(pending), 1);
        }
        else if (!pending) {
            // We don't have a pending stream for this client yet, so add it to pendingStreams
            this.pendingStreams.push({
                streamId: stream.id,
                stream: stream,
                streamType: undefined
            });
            this.removePendingStreamIfTimedOut(stream.id);
        }
        else {
            console.warn("[multiplayer] Invalid pending stream state");
        }
    }
    streamTypeFromString(type) {
        switch (type) {
            case "microphone":
                return StreamType.microphone;
            case "camera":
                return StreamType.camera;
            case "screen":
                return StreamType.screen;
            case "player3dView":
                return StreamType.player3dView;
            default:
                return undefined;
        }
    }
    addNewRemoteStream(remoteStream) {
        // Workaround for mic streams that arrive without audio tracks
        // TODO: Find out why streams sometimes arrive without tracks and prevent it. See Ticket FVR-758
        if (remoteStream.type === StreamType.microphone && remoteStream.stream.getAudioTracks().length === 0) {
            console.warn("[multiplayer] addNewRemoteStream: microphone stream has no audio tracks and will be ignored", remoteStream.stream);
            return;
        }
        this.remoteStreams.push(remoteStream);
        if (remoteStream.type === StreamType.screen || remoteStream.type === StreamType.player3dView) {
            // In case of screen and player3dView streams, we need to inform scripts about the start of the stream
            const message = new ModelMediaStreamStartedMessage(remoteStream.surfaceUid, remoteStream.clientId);
            this.bus.send(message);
        }
        this.onUpdateDelegate.invoke();
    }
    handleMediaStreamEndedMessage(stream) {
        const remoteStream = this.remoteStreams.find((remoteStream) => remoteStream.stream.id === stream.id);
        if (remoteStream) {
            remoteStream.stream.getTracks().forEach((track) => {
                track.stop();
            });
            this.remoteStreams.splice(this.remoteStreams.indexOf(remoteStream), 1);
            if (remoteStream.type === StreamType.screen || remoteStream.type === StreamType.player3dView) {
                // In case of screen and player3dView streams, we need to inform scripts about the end of the stream
                const message = new ModelMediaStreamEndedMessage(remoteStream.surfaceUid);
                this.bus.send(message);
            }
            this.onUpdateDelegate.invoke();
        }
        else {
            console.warn("[multiplayer] onended without stream");
        }
    }
}
const remoteStreamingService = new RemoteStreamingService();
remoteStreamingService.init();
export { remoteStreamingService };
